import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { adminSettingsApi } from "@profitlens/api-client";
import type { EmailTemplateOut } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card, CardContent, CardHeader } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import { Switch } from "@profitlens/ui/switch";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const ALL = "__all__";

const FEATURES = ["email", "ads_parse", "title_opt"] as const;
const FEATURE_KEYS: Record<string, string> = {
  email: "settings.featureEmail",
  ads_parse: "settings.featureAdsParse",
  title_opt: "settings.featureTitleOpt",
};

export function TemplatesPage() {
  const { t } = useTranslation();
  const [feature, setFeature] = useState<string>(ALL);
  const [locale, setLocale] = useState<string>(ALL);
  const [editing, setEditing] = useState<EmailTemplateOut | "new" | null>(null);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ["admin", "email-templates", { feature, locale }],
    queryFn: () =>
      adminSettingsApi.listEmailTemplates({
        feature: feature === ALL ? undefined : feature,
        locale: locale === ALL ? undefined : locale,
      }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminSettingsApi.deleteEmailTemplate(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
    },
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("settings.templatesTitle")}
        subtitle={t("settings.templatesSubtitle")}
        actions={
          <>
            <Select value={feature} onValueChange={setFeature}>
              <SelectTrigger className="w-40" aria-label={t("settings.filterFeature")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("common.all")}</SelectItem>
                {FEATURES.map((f) => (
                  <SelectItem key={f} value={f}>
                    {t(FEATURE_KEYS[f])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger className="w-32" aria-label={t("settings.filterLocale")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("common.all")}</SelectItem>
                <SelectItem value="zh-CN">{t("settings.localeZh")}</SelectItem>
                <SelectItem value="en-US">{t("settings.localeEn")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" />
              {t("settings.templateAdd")}
            </Button>
          </>
        }
      />

      {list.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data && list.data.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("settings.templatesEmpty")}</Card>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {list.data?.map((tpl) => (
            <Card key={tpl.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 p-4 pb-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{tpl.name}</p>
                  <p className="mt-0.5 text-xs text-muted">
                    {t(FEATURE_KEYS[tpl.feature] ?? tpl.feature)} · {tpl.locale} ·{" "}
                    {t("settings.templateVersion", { version: tpl.version })}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {tpl.is_default ? <Badge>{t("settings.templateDefault")}</Badge> : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 p-4 pt-0">
                <p className="line-clamp-2 text-xs text-muted">{tpl.system_prompt}</p>
                <div className="flex items-center justify-between text-xs text-muted">
                  <span>
                    {tpl.last_used_at
                      ? t("settings.templateLastUsed", { time: formatDateTime(tpl.last_used_at) })
                      : t("settings.templateNeverUsed")}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setEditing(tpl)}>
                      <Pencil className="h-3.5 w-3.5" />
                      {t("common.edit")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(t("common.confirmDelete"))) remove.mutate(tpl.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-danger" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TemplateDialog editing={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

const textareaClass =
  "min-h-28 w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-foreground placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50";

function TemplateDialog({
  editing,
  onClose,
}: {
  editing: EmailTemplateOut | "new" | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isNew = editing === "new";
  const existing = editing !== null && editing !== "new" ? editing : null;

  const [feature, setFeature] = useState<string>("email");
  const [locale, setLocale] = useState("zh-CN");
  const [name, setName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [variablesText, setVariablesText] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setFeature(existing.feature);
      setLocale(existing.locale);
      setName(existing.name);
      setSystemPrompt(existing.system_prompt);
      setUserPrompt(existing.user_prompt);
      setVariablesText(existing.variables ? JSON.stringify(existing.variables, null, 2) : "");
      setIsDefault(existing.is_default);
    } else {
      setFeature("email");
      setLocale("zh-CN");
      setName("");
      setSystemPrompt("");
      setUserPrompt("");
      setVariablesText("");
      setIsDefault(false);
    }
    setError(null);
  }, [editing, existing]);

  const parseVariables = (): Record<string, unknown> | null => {
    if (!variablesText.trim()) return null;
    const parsed: unknown = JSON.parse(variablesText);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      throw new Error("variables must be an object");
    }
    return parsed as Record<string, unknown>;
  };

  const save = useMutation({
    mutationFn: () => {
      const variables = parseVariables();
      if (isNew) {
        return adminSettingsApi.createEmailTemplate({
          feature: feature as "email" | "ads_parse" | "title_opt",
          locale,
          name,
          system_prompt: systemPrompt,
          user_prompt: userPrompt,
          variables,
          is_default: isDefault,
        });
      }
      if (!existing) throw new Error("no template");
      return adminSettingsApi.patchEmailTemplate(existing.id, {
        locale,
        name,
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        variables,
        is_default: isDefault,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      onClose();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isNew ? t("settings.templateAdd") : t("settings.templateEdit")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>{t("settings.templateFeature")}</Label>
              <Select value={feature} onValueChange={setFeature} disabled={!isNew}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FEATURES.map((f) => (
                    <SelectItem key={f} value={f}>
                      {t(FEATURE_KEYS[f])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.templateLocale")}</Label>
              <Select value={locale} onValueChange={setLocale}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zh-CN">{t("settings.localeZh")}</SelectItem>
                  <SelectItem value="en-US">{t("settings.localeEn")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.templateName")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("settings.templateSystem")}</Label>
            <textarea
              className={textareaClass}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("settings.templateUser")}</Label>
            <textarea
              className={textareaClass}
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("settings.templateVariables")}</Label>
            <textarea
              className={textareaClass + " min-h-16"}
              value={variablesText}
              onChange={(e) => setVariablesText(e.target.value)}
              placeholder='{"customer_name": "客户姓名"}'
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
            {t("settings.templateDefault")}
          </label>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={save.isPending || !name} onClick={() => save.mutate()}>
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
